/**
 * TypeScript wrapper for the WASM subdivision surface module.
 *
 * The Rust kernel is polygon-native: it consumes `PolygonTopology`
 * (offsets + corners) and emits a polygon topology where every output
 * face is a quad. `subdiv_type` selects between Catmull-Clark (smooth)
 * and Quad Midpoint ("Simple") — both refine every n-gon face into n
 * quads, so the final mesh has no triangles regardless of the input.
 *
 * Per-edge crease weights flow through here: when the input geometry
 * carries `edges` + `edgeCreases`, the flat pair list is passed verbatim
 * to the Rust kernel, which applies Hoppe 1994 piecewise-smooth rules
 * adapted to Catmull-Clark during smoothing. Output creases are dropped
 * — subsurf retopologises, and the crease set is re-derivable from
 * source attributes.
 */

import type { BlendModifier } from "../blend-reader/types/BlendModifier.ts";
import {
  faceProvenanceFromSourceMap,
  provenanceFromSourceMap,
} from "../modeling/provenance.ts";
import { buildEdgesAndCornerEdges } from "../modeling/topology.ts";
import {
  asSharedTriangleIndices,
  type PolygonTopology,
} from "../modeling/types.ts";
import type { ModifierGeometry } from "../viewport/modifiers/modifierTypes.ts";
import { subsurf_core as wasmSubsurfCore } from "./pkg/poseref_wasm.js";

const EMPTY_F32 = new Float32Array(0);
const EMPTY_U32 = new Uint32Array(0);

/**
 * Build a triangle-only polygon topology from a shared-vertex triangle
 * index buffer. Used when the caller has no explicit `topology` — each
 * set of 3 indices becomes one 3-corner face. Catmull-Clark then refines
 * every triangle into 3 quads, matching Blender's behavior on triangle
 * input.
 */
function trianglesToPolyInput(indices: Uint32Array): {
  offsets: Uint32Array;
  corners: Uint32Array;
} {
  const triCount = (indices.length / 3) | 0;
  const offsets = new Uint32Array(triCount + 1);
  for (let i = 0; i <= triCount; i++) offsets[i] = i * 3;
  return { offsets, corners: new Uint32Array(indices) };
}

function buildSubsurfFaceSourceMap(
  inputOffsets: Uint32Array,
  levels: number,
): Int32Array {
  const inputFaceCount = inputOffsets.length - 1;
  let parentForFace = new Int32Array(inputFaceCount);
  let faceSizes = new Uint32Array(inputFaceCount);
  for (let f = 0; f < inputFaceCount; f++) {
    parentForFace[f] = f;
    faceSizes[f] = inputOffsets[f + 1] - inputOffsets[f];
  }

  for (let level = 0; level < levels; level++) {
    let nextFaceCount = 0;
    for (let f = 0; f < faceSizes.length; f++) nextFaceCount += faceSizes[f];

    const nextParents = new Int32Array(nextFaceCount);
    const nextSizes = new Uint32Array(nextFaceCount);
    let cursor = 0;
    for (let f = 0; f < parentForFace.length; f++) {
      const childCount = faceSizes[f];
      for (let child = 0; child < childCount; child++) {
        nextParents[cursor] = parentForFace[f];
        nextSizes[cursor] = 4;
        cursor++;
      }
    }
    parentForFace = nextParents;
    faceSizes = nextSizes;
  }

  return parentForFace;
}

export function subsurfCore(
  geo: ModifierGeometry,
  mod_: Extract<BlendModifier, { kind: "subsurf" }>,
): ModifierGeometry {
  const levels = mod_.levels;
  if (levels <= 0) return geo;

  const vertCount = geo.positions.length / 3;

  // Polygon input: prefer explicit topology (quads/n-gons preserved);
  // otherwise fall back to a triangle-only polygon built from the
  // shared-vertex index buffer. Either way, the kernel emits all quads.
  const polyIn = geo.topology
    ? { offsets: geo.topology.offsets, corners: geo.topology.corners }
    : trianglesToPolyInput(geo.indices as Uint32Array);

  // Concatenate morph arrays into a single Float32Array
  let morphData: Float32Array;
  const morphCount = geo.morphArrays.length;
  if (morphCount > 0) {
    morphData = new Float32Array(morphCount * vertCount * 3);
    for (let m = 0; m < morphCount; m++) {
      morphData.set(geo.morphArrays[m], m * vertCount * 3);
    }
  } else {
    morphData = EMPTY_F32;
  }

  // Polygon-edge + crease arrays for Hoppe piecewise-smooth rules.
  // Only meaningful when both are present AND at least one weight is
  // non-zero — the Rust side short-circuits when the crease slice is empty.
  const hasCreases = !!(
    geo.edges &&
    geo.edges.length > 0 &&
    geo.edgeCreases &&
    geo.edgeCreases.length > 0
  );
  const polyEdges = hasCreases ? geo.edges! : EMPTY_U32;
  const edgeCreases = hasCreases ? geo.edgeCreases! : EMPTY_F32;

  const result = wasmSubsurfCore(
    geo.positions,
    polyIn.offsets,
    polyIn.corners,
    geo.uvs ?? EMPTY_F32,
    morphData,
    morphCount,
    geo.skinIndex ?? EMPTY_F32,
    geo.skinWeight ?? EMPTY_F32,
    geo.sculptDisplacement ?? EMPTY_F32,
    polyEdges,
    edgeCreases,
    mod_.subdivType,
    levels,
  );

  // Split morph data back into individual arrays
  const outMorphCount = result.morph_count;
  const outMorphData = result.morph_data;
  const outVertCount = result.positions.length / 3;
  const outMorphArrays: Float32Array[] = [];
  for (let m = 0; m < outMorphCount; m++) {
    const start = m * outVertCount * 3;
    const end = start + outVertCount * 3;
    outMorphArrays.push(outMorphData.slice(start, end));
  }

  // Provenance: the Rust subsurf kernel preserves original vertices at
  // output indices [0..vertCount); face-points and edge-points appear at
  // [vertCount..). Original verts keep their source parent (themselves);
  // novel verts are orphans whose inheritance defaults are handled by the
  // reconciliation layer. Matches Blender's behaviour on subsurf-apply:
  // original-vert attributes persist, new verts take per-attribute defaults.
  const srcForNew = new Int32Array(outVertCount).fill(-1);
  for (let i = 0; i < vertCount && i < outVertCount; i++) srcForNew[i] = i;
  const provenance = provenanceFromSourceMap(vertCount, srcForNew);

  // Assemble the output polygon topology. The Rust kernel emits offsets
  // + corners; we derive edges / cornerEdges / edgeFlags via the shared
  // `build_edges_from_topology` helper so downstream modifiers (bevel,
  // solidify follow-ups, etc.) read a complete PolygonTopology.
  const outOffsets = result.offsets;
  const outCorners = result.corners;
  const { edges, cornerEdges, edgeFlags } = buildEdgesAndCornerEdges(
    outOffsets,
    outCorners,
  );
  const outTopology: PolygonTopology = {
    offsets: outOffsets,
    corners: outCorners,
    edges,
    cornerEdges,
    edgeFlags,
  };
  const faceProvenance = faceProvenanceFromSourceMap(
    polyIn.offsets.length - 1,
    buildSubsurfFaceSourceMap(polyIn.offsets, levels),
  );

  const outGeo: ModifierGeometry = {
    positions: result.positions,
    indices: asSharedTriangleIndices(result.indices),
    normals: null,
    uvs: result.uvs.length > 0 ? result.uvs : null,
    morphArrays: outMorphArrays,
    skinIndex: result.skin_index.length > 0 ? result.skin_index : null,
    skinWeight: result.skin_weight.length > 0 ? result.skin_weight : null,
    sculptDisplacement:
      result.sculpt_displacement.length > 0 ? result.sculpt_displacement : null,
    groups: [],
    morphTargetsRelative: geo.morphTargetsRelative,
    morphNames: geo.morphNames,
    // Subsurf retopologises — per-edge flags and creases do not survive,
    // but the fresh edges array is still meaningful (e.g. bevel downstream).
    edges,
    edgeFlags,
    edgeCreases: null,
    skinRadii: null,
    topology: outTopology,
    faceProvenance,
    provenance,
  };

  result.free();
  return outGeo;
}
